/**
 * Native PlayCanvas world content — panels, interactables, and zones in the
 * same render graph as the gsplat scene (correct depth, no Three.js overlay).
 */
import * as pc from 'playcanvas';
import type {
  InteractionContext,
  LoadedScene,
  WorldLabel,
  ModelPanelDef,
  PanelBase,
  SimplePanelDef,
  TexturedPanelDef,
  VideoModelPanelDef,
  VideoPanelDef,
  WorldInteractable,
  WorldPanel,
  WorldZone,
  TextureScrollDef,
} from '@viewer/world/worldTypes';
import {
  hasTextureScroll,
  textureScrollOffset,
  wrapTextureOffset,
} from '@viewer/playcanvas/textureScroll';
import { runInteractionAction } from '@viewer/playcanvas/interactionAction.mjs';

const PANEL_TEX_SIZE = 512;
const LABEL_TEX_SIZE = 512;

interface PanelState {
  entity: pc.Entity;
  intendedVisible: boolean;
  video?: HTMLVideoElement;
  videoTexture?: pc.Texture;
  material?: pc.StandardMaterial;
  materials?: pc.StandardMaterial[];
  baseOpacity: number;
  baseEmissive: number;
  videoFadeInMs: number;
  videoFadeAnim: { startMs: number; durationMs: number } | null;
  /** AnimComponent state to play when the panel becomes visible. */
  animPlayState?: string;
  /** WebGPU blit target — video elements are not reliable as copyExternalImage sources. */
  videoScratchCanvas?: HTMLCanvasElement;
  /** True when the video produced a frame we haven't uploaded yet (rVFC-driven). */
  videoFrameDirty?: boolean;
  /** Teardown for the requestVideoFrameCallback loop, if registered. */
  videoFrameCancel?: () => void;
  /** Authored autoplay intent, before runtime quality/settings policy is applied. */
  videoAutoplayAllowed?: boolean;
  /** Paused placeholder state; tapping/clicking the panel starts playback. */
  videoClickToPlay?: boolean;
  /** Entrance fade when the world becomes interactive. */
  worldRevealFade?: { startMs: number; durationMs: number } | null;
  worldRevealAlpha: number;
  proximityAlpha: number;
  proximityPosition?: [number, number, number];
  proximityRevealDistance?: number;
  proximityFullOpacityDistance?: number;
  materialBaselines?: MaterialBaseline[];
}

interface TextureScrollState {
  entity: pc.Entity;
  material: pc.StandardMaterial;
  scroll: TextureScrollDef;
  elapsedSeconds: number;
  baseDiffuseOffset: pc.Vec2;
  baseEmissiveOffset: pc.Vec2;
  baseOpacityOffset: pc.Vec2;
}

type VideoFrameCallbacks = {
  requestVideoFrameCallback?: (cb: () => void) => number;
  cancelVideoFrameCallback?: (handle: number) => void;
};

/**
 * Drive a per-video "dirty" flag from requestVideoFrameCallback so we only upload a
 * texture when the video actually advances a frame (e.g. 24–30 fps), instead of every
 * render tick (up to 120 fps). Falls back to always-dirty when rVFC is unavailable.
 */
function watchVideoFrames(video: HTMLVideoElement, onFrame: () => void): (() => void) | undefined {
  const vfc = video as HTMLVideoElement & VideoFrameCallbacks;
  if (typeof vfc.requestVideoFrameCallback !== 'function') return undefined;
  let handle = 0;
  let cancelled = false;
  const loop = () => {
    if (cancelled) return;
    onFrame();
    handle = vfc.requestVideoFrameCallback!(loop);
  };
  handle = vfc.requestVideoFrameCallback(loop);
  return () => {
    cancelled = true;
    vfc.cancelVideoFrameCallback?.(handle);
  };
}

interface InteractableState {
  entity: pc.Entity;
  def: WorldInteractable;
  normalMaterial: pc.StandardMaterial;
  hoverMaterial: pc.StandardMaterial;
  normalBaseline: MaterialBaseline;
  hoverBaseline: MaterialBaseline;
  worldRevealFade?: { startMs: number; durationMs: number } | null;
}

function evalHandler(code: string, ctx: InteractionContext): void {
  if (!runInteractionAction(code, ctx)) {
    console.warn('[PlayCanvasWorld] rejected unsupported handler:', code);
  }
}

function loadAsset(
  app: pc.AppBase,
  name: string,
  type: 'texture' | 'container',
  url: string,
): Promise<pc.Asset> {
  const asset = new pc.Asset(name, type, { url });
  app.assets.add(asset);
  return new Promise((resolve, reject) => {
    asset.once('load', () => resolve(asset));
    asset.once('error', (err: string) => reject(new Error(err)));
    app.assets.load(asset);
  });
}

function makePanelCanvas(color: string, text?: string, textColor?: string): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = PANEL_TEX_SIZE;
  canvas.height = PANEL_TEX_SIZE;
  const ctx = canvas.getContext('2d')!;
  ctx.fillStyle = color;
  ctx.fillRect(0, 0, PANEL_TEX_SIZE, PANEL_TEX_SIZE);
  ctx.strokeStyle = 'rgba(255,255,255,0.25)';
  ctx.lineWidth = 10;
  ctx.strokeRect(6, 6, PANEL_TEX_SIZE - 12, PANEL_TEX_SIZE - 12);
  if (text) {
    ctx.fillStyle = textColor ?? '#000000';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    let fontSize = 96;
    ctx.font = `bold ${fontSize}px sans-serif`;
    while (ctx.measureText(text).width > PANEL_TEX_SIZE - 80 && fontSize > 24) {
      fontSize -= 4;
      ctx.font = `bold ${fontSize}px sans-serif`;
    }
    ctx.fillText(text, PANEL_TEX_SIZE / 2, PANEL_TEX_SIZE / 2);
  }
  return canvas;
}

function videoFrameReady(video: HTMLVideoElement): boolean {
  return (
    video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA &&
    video.videoWidth > 0 &&
    video.videoHeight > 0 &&
    !video.seeking
  );
}

function panelVideoNeedsRender(state: PanelState): boolean {
  return Boolean(
    state.video &&
      !state.videoClickToPlay &&
      !state.video.paused &&
      !state.video.ended &&
      state.videoFrameDirty !== false,
  );
}

export function shouldPanelVideoAutoplay(
  panelAutoplay: boolean | undefined,
  mediaAutoplayEnabled: boolean,
): boolean {
  return mediaAutoplayEnabled && panelAutoplay !== false;
}

export function shouldPanelVideoStartClickToPlay(
  panelAutoplay: boolean | undefined,
  mediaAutoplayEnabled: boolean,
): boolean {
  return !shouldPanelVideoAutoplay(panelAutoplay, mediaAutoplayEnabled);
}

/**
 * Upload a video frame to a PlayCanvas texture.
 * WebGPU cannot reliably copy HTMLVideoElement each frame (browser console spam / early return);
 * blit to a scratch canvas first. WebGL2 keeps the video element as the texture source.
 */
function uploadVideoTexture(
  device: pc.GraphicsDevice,
  texture: pc.Texture,
  video: HTMLVideoElement,
  scratch?: HTMLCanvasElement,
): HTMLCanvasElement | undefined {
  if (!videoFrameReady(video)) return scratch;

  if (device.isWebGPU) {
    const canvas = scratch ?? document.createElement('canvas');
    const w = video.videoWidth;
    const h = video.videoHeight;
    if (canvas.width !== w) canvas.width = w;
    if (canvas.height !== h) canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) return canvas;
    try {
      ctx.drawImage(video, 0, 0, w, h);
      texture.setSource(canvas);
      texture.upload();
    } catch {
      // Tainted/cross-origin or undecoded frame — skip this frame.
    }
    return canvas;
  }

  texture.setSource(video);
  texture.upload();
  return scratch;
}

function uploadVideoPlaceholderTexture(
  texture: pc.Texture,
  video: HTMLVideoElement,
  scratch?: HTMLCanvasElement,
): HTMLCanvasElement {
  const canvas = scratch ?? document.createElement('canvas');
  const w = Math.max(2, video.videoWidth || 960);
  const h = Math.max(2, video.videoHeight || 540);
  if (canvas.width !== w) canvas.width = w;
  if (canvas.height !== h) canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) return canvas;

  const gradient = ctx.createLinearGradient(0, 0, w, h);
  gradient.addColorStop(0, '#14191f');
  gradient.addColorStop(1, '#05070a');
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, w, h);

  if (videoFrameReady(video)) {
    try {
      ctx.drawImage(video, 0, 0, w, h);
    } catch {
      // If the frame cannot be drawn, keep the neutral fallback.
    }
  }

  ctx.fillStyle = 'rgba(0, 0, 0, 0.24)';
  ctx.fillRect(0, 0, w, h);

  const cx = w / 2;
  const cy = h / 2;
  const r = Math.max(42, Math.min(w, h) * 0.13);
  ctx.save();
  ctx.shadowColor = 'rgba(0, 0, 0, 0.42)';
  ctx.shadowBlur = r * 0.24;
  ctx.fillStyle = 'rgba(255, 255, 255, 0.22)';
  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();

  ctx.strokeStyle = 'rgba(255, 255, 255, 0.7)';
  ctx.lineWidth = Math.max(2, r * 0.045);
  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, Math.PI * 2);
  ctx.stroke();

  ctx.fillStyle = 'rgba(255, 255, 255, 0.92)';
  ctx.beginPath();
  ctx.moveTo(cx - r * 0.22, cy - r * 0.36);
  ctx.lineTo(cx - r * 0.22, cy + r * 0.36);
  ctx.lineTo(cx + r * 0.44, cy);
  ctx.closePath();
  ctx.fill();

  texture.setSource(canvas);
  texture.upload();
  return canvas;
}

function createVideoTexture(device: pc.GraphicsDevice, video: HTMLVideoElement): pc.Texture {
  const texture = new pc.Texture(device, {
    width: Math.max(2, video.videoWidth || 2),
    height: Math.max(2, video.videoHeight || 2),
    format: pc.PIXELFORMAT_RGBA8,
    mipmaps: false,
  });
  if (!device.isWebGPU) {
    texture.setSource(video);
  }
  return texture;
}

function textureFromCanvas(device: pc.GraphicsDevice, canvas: HTMLCanvasElement): pc.Texture {
  const texture = new pc.Texture(device, {
    width: canvas.width,
    height: canvas.height,
    format: pc.PIXELFORMAT_RGBA8,
    mipmaps: false,
    minFilter: pc.FILTER_LINEAR,
    magFilter: pc.FILTER_LINEAR,
  });
  texture.setSource(canvas);
  texture.upload();
  return texture;
}

const WORLD_DRAW_ORDER = 0;
/** Use 0 for authored world.json positions; depth prepass handles splat occlusion. */
const PANEL_DEPTH_BIAS = 0;
const ALPHA_CUTOUT_THRESHOLD = 0.2;

type AlphaCutoutMaterial = pc.StandardMaterial & { _giiAlphaCutoutThreshold?: number };

function applyTransform(
  entity: pc.Entity,
  panel: PanelBase,
  basisXDeg: number,
  uniformScale = 1,
  depthBias = PANEL_DEPTH_BIAS,
): void {
  const w = panel.width ?? 1;
  const h = panel.height ?? 1;
  const rx = (panel.rotation?.[0] ?? 0) + basisXDeg - 90;
  const ry = panel.rotation?.[1] ?? 0;
  const rz = panel.rotation?.[2] ?? 0;
  entity.setLocalEulerAngles(rx, ry, rz);
  const sx = w * uniformScale;
  const sz = h * uniformScale;
  entity.setLocalScale(sx, 1, sz);

  const [px, py, pz] = panel.position;
  if (depthBias > 0) {
    const n = entity.forward;
    entity.setLocalPosition(px + n.x * depthBias, py + n.y * depthBias, pz + n.z * depthBias);
  } else {
    entity.setLocalPosition(px, py, pz);
  }
}

/** GLB roots — uniform scale and basis rotation (no plane −90° correction). */
function applyModelTransform(
  entity: pc.Entity,
  panel: PanelBase & { scale?: number },
  basisXDeg: number,
  uniformScale = 1,
  depthBias = PANEL_DEPTH_BIAS,
): void {
  const rx = (panel.rotation?.[0] ?? 0) + basisXDeg;
  const ry = panel.rotation?.[1] ?? 0;
  const rz = panel.rotation?.[2] ?? 0;
  entity.setLocalEulerAngles(rx, ry, rz);
  entity.setLocalScale(uniformScale, uniformScale, uniformScale);

  const [px, py, pz] = panel.position;
  if (depthBias > 0) {
    const n = entity.forward;
    entity.setLocalPosition(px + n.x * depthBias, py + n.y * depthBias, pz + n.z * depthBias);
  } else {
    entity.setLocalPosition(px, py, pz);
  }
}

/** Wire GLB container clips onto a render entity (AnimTrack + AnimComponent). */
function setupModelAnimations(
  entity: pc.Entity,
  animAssets: pc.Asset[],
  options: { animation?: string; autoplay?: boolean; loop?: boolean },
): string | undefined {
  if (animAssets.length === 0) return undefined;

  const loop = options.loop !== false;
  const autoplay = options.autoplay !== false;
  const named = options.animation?.trim();

  entity.addComponent('anim', {
    activate: autoplay,
    playing: false,
  });

  const entries: { stateName: string; track: pc.AnimTrack }[] = [];
  for (const animAsset of animAssets) {
    const track = animAsset.resource as pc.AnimTrack | undefined;
    if (!track) continue;
    const stateName = track.name || animAsset.name.split('/').pop() || 'animation';
    entity.anim!.assignAnimation(stateName, track, 'Base', 1, loop);
    entries.push({ stateName, track });
  }
  if (entries.length === 0) return undefined;

  let playEntries = entries;
  if (named) {
    const filtered = entries.filter((e) => e.stateName === named || e.track.name === named);
    if (filtered.length === 0) {
      console.warn(`[PlayCanvasWorld] model animation not found: ${named}`);
    } else {
      playEntries = filtered;
    }
  }

  const playState = playEntries[0]!.stateName;
  if (autoplay) {
    entity.anim!.playing = true;
    entity.anim!.baseLayer?.play(playState);
  }
  return playState;
}

/** Emissive-mapped panels need a non-zero intensity to be visible; honour explicit values below 1. */
function resolvePanelEmissive(emissive: number | undefined): number {
  const e = emissive ?? 0;
  return e > 0 ? e : 1;
}

function resolveAlphaCutout(alphaCutout: number | undefined): number {
  if (typeof alphaCutout !== 'number' || !Number.isFinite(alphaCutout)) {
    return ALPHA_CUTOUT_THRESHOLD;
  }
  return Math.max(0.001, Math.min(0.999, alphaCutout));
}

function alphaCutoutThreshold(mat: pc.StandardMaterial): number {
  const tagged = (mat as AlphaCutoutMaterial)._giiAlphaCutoutThreshold;
  if (typeof tagged === 'number' && tagged > 0) return tagged;
  return mat.opacityMap && mat.alphaTest > 0 ? mat.alphaTest : 0;
}

function isAlphaCutoutMaterial(mat: pc.StandardMaterial): boolean {
  return Boolean(mat.opacityMap) && alphaCutoutThreshold(mat) > 0;
}

function configureAlphaCutoutMaterial(
  mat: pc.StandardMaterial,
  texture: pc.Texture,
  alphaCutout?: number,
): void {
  const threshold = resolveAlphaCutout(alphaCutout);
  (mat as AlphaCutoutMaterial)._giiAlphaCutoutThreshold = threshold;
  mat.opacityMap = texture;
  mat.opacityMapChannel = 'a';
  mat.alphaTest = threshold;
  mat.blendType = pc.BLEND_NONE;
  mat.depthWrite = true;
  mat.depthTest = true;
}

function createPlaneMaterial(
  diffuse: pc.Texture | null,
  emissiveIntensity: number,
  opacity = 1,
  options: { alphaFromTexture?: boolean; repeatTexture?: boolean; alphaCutout?: number } = {},
): pc.StandardMaterial {
  const mat = new pc.StandardMaterial();
  mat.useLighting = false;
  mat.cull = pc.CULLFACE_NONE;
  mat.diffuse = new pc.Color(0, 0, 0);
  mat.opacity = opacity;
  const opaque = opacity >= 0.999 && !options.alphaFromTexture;
  mat.blendType = opaque ? pc.BLEND_NONE : pc.BLEND_NORMAL;
  mat.depthWrite = opaque;
  mat.depthTest = true;
  if (diffuse) {
    if (options.repeatTexture) setTextureRepeat(diffuse);
    if (options.alphaFromTexture) {
      mat.diffuse = new pc.Color(1, 1, 1);
      mat.diffuseMap = diffuse;
    }
    mat.emissiveMap = diffuse;
    mat.emissive = new pc.Color(1, 1, 1);
    mat.emissiveIntensity = resolvePanelEmissive(emissiveIntensity);
    if (options.alphaFromTexture) {
      configureAlphaCutoutMaterial(mat, diffuse, options.alphaCutout);
      if (opacity < 0.999) {
        mat.alphaTest = 0;
        mat.blendType = pc.BLEND_NORMAL;
        mat.depthWrite = false;
      }
    }
  }
  mat.update();
  return mat;
}

function setTextureRepeat(texture: pc.Texture | null | undefined): void {
  if (!texture) return;
  texture.addressU = pc.ADDRESS_REPEAT;
  texture.addressV = pc.ADDRESS_REPEAT;
}

/** Opaque panels must use BLEND_NONE so they land in the depth prepass; fades use transparent blend. */
function applyPanelMaterialOpacity(
  mat: pc.StandardMaterial,
  opacity: number,
  emissiveIntensity: number,
): void {
  const cutout = isAlphaCutoutMaterial(mat);
  const opaque = opacity >= 0.999 && (!mat.opacityMap || cutout);
  mat.opacity = opacity;
  mat.emissiveIntensity = emissiveIntensity;
  if (cutout) {
    mat.alphaTest = opaque ? alphaCutoutThreshold(mat) : 0;
  }
  mat.blendType = opaque ? pc.BLEND_NONE : pc.BLEND_NORMAL;
  mat.depthWrite = opaque;
  mat.depthTest = true;
  mat.update();
}

function forEachPanelMaterial(
  state: PanelState,
  fn: (material: pc.StandardMaterial) => void,
): void {
  const seen = new Set<pc.StandardMaterial>();
  if (state.material) {
    seen.add(state.material);
    fn(state.material);
  }
  for (const material of state.materials ?? []) {
    if (seen.has(material)) continue;
    seen.add(material);
    fn(material);
  }
}

function captureMaterialBaseline(material: pc.StandardMaterial): MaterialBaseline {
  return {
    material,
    opacity: material.opacity,
    emissiveIntensity: material.emissiveIntensity,
    blendType: material.blendType,
    depthWrite: material.depthWrite,
    depthTest: material.depthTest,
    alphaTest: material.alphaTest,
  };
}

function applyMaterialBaselineAlpha(baseline: MaterialBaseline, alpha: number): void {
  const a = Math.max(0, Math.min(1, alpha));
  const material = baseline.material;
  material.opacity = baseline.opacity * a;
  material.emissiveIntensity = baseline.emissiveIntensity * a;
  if (a >= 0.999) {
    material.blendType = baseline.blendType;
    material.depthWrite = baseline.depthWrite;
    material.depthTest = baseline.depthTest;
    material.alphaTest = baseline.alphaTest;
  } else {
    material.blendType = pc.BLEND_NORMAL;
    material.depthWrite = false;
    material.depthTest = baseline.depthTest;
    material.alphaTest = 0;
  }
  material.update();
}

function smoothstep01(value: number): number {
  const t = Math.max(0, Math.min(1, value));
  return t * t * (3 - 2 * t);
}

function panelProximityAlpha(state: PanelState, cameraPos: pc.Vec3): number {
  const position = state.proximityPosition;
  const revealDistance = state.proximityRevealDistance;
  if (!position || revealDistance === undefined) return 1;
  const fullDistance = Math.min(
    revealDistance,
    Math.max(0, state.proximityFullOpacityDistance ?? revealDistance * 0.25),
  );
  const dx = cameraPos.x - position[0];
  const dz = cameraPos.z - position[2];
  const distanceSq = dx * dx + dz * dz;
  if (distanceSq >= revealDistance * revealDistance) return 0;
  if (distanceSq <= fullDistance * fullDistance) return 1;
  const distance = Math.sqrt(distanceSq);
  return smoothstep01((revealDistance - distance) / (revealDistance - fullDistance));
}

function resolvePanelProximity(
  panel: PanelBase,
): Pick<
  PanelState,
  | 'proximityAlpha'
  | 'proximityPosition'
  | 'proximityRevealDistance'
  | 'proximityFullOpacityDistance'
> {
  const revealDistance =
    panel.proximity &&
    Number.isFinite(panel.proximity.revealDistance) &&
    panel.proximity.revealDistance > 0
      ? panel.proximity.revealDistance
      : undefined;
  const fullOpacityDistance =
    panel.proximity &&
    Number.isFinite(panel.proximity.fullOpacityDistance) &&
    (panel.proximity.fullOpacityDistance ?? 0) >= 0
      ? panel.proximity.fullOpacityDistance
      : undefined;
  return {
    proximityAlpha: revealDistance === undefined ? 1 : 0,
    proximityPosition: panel.position,
    proximityRevealDistance: revealDistance,
    proximityFullOpacityDistance: fullOpacityDistance,
  };
}

function collectUniqueStandardMaterials(root: pc.Entity): pc.StandardMaterial[] {
  const materials: pc.StandardMaterial[] = [];
  const seen = new Set<pc.StandardMaterial>();
  for (const node of collectRenderEntities(root)) {
    if (!node.render) continue;
    for (const mi of node.render.meshInstances) {
      if (!(mi.material instanceof pc.StandardMaterial)) continue;
      if (seen.has(mi.material)) continue;
      seen.add(mi.material);
      materials.push(mi.material);
    }
  }
  return materials;
}

function ownEntityTreeStandardMaterials(root: pc.Entity): pc.StandardMaterial[] {
  const clones = new Map<pc.StandardMaterial, pc.StandardMaterial>();
  for (const node of collectRenderEntities(root)) {
    if (!node.render) continue;
    for (const mi of node.render.meshInstances) {
      if (!(mi.material instanceof pc.StandardMaterial)) continue;
      let clone = clones.get(mi.material);
      if (!clone) {
        clone = mi.material.clone() as pc.StandardMaterial;
        clones.set(mi.material, clone);
      }
      mi.material = clone;
    }
  }
  return Array.from(clones.values());
}

function applyModelTextureOverride(
  materials: pc.StandardMaterial[],
  texture: pc.Texture,
  emissiveIntensity: number,
  opacity: number,
  options: { alphaFromTexture?: boolean; repeatTexture?: boolean; alphaCutout?: number } = {},
): void {
  if (options.repeatTexture) setTextureRepeat(texture);
  for (const material of materials) {
    material.useLighting = false;
    material.cull = pc.CULLFACE_NONE;
    material.diffuse = new pc.Color(1, 1, 1);
    material.diffuseMap = texture;
    material.emissive = new pc.Color(1, 1, 1);
    material.emissiveMap = texture;
    material.emissiveIntensity = resolvePanelEmissive(emissiveIntensity);
    material.opacity = opacity;
    material.depthTest = true;

    if (options.alphaFromTexture) {
      configureAlphaCutoutMaterial(material, texture, options.alphaCutout);
      if (opacity < 0.999) {
        material.alphaTest = 0;
        material.blendType = pc.BLEND_NORMAL;
        material.depthWrite = false;
      }
    } else {
      material.blendType = opacity >= 0.999 ? pc.BLEND_NONE : pc.BLEND_NORMAL;
      material.depthWrite = opacity >= 0.999;
    }
    material.update();
  }
}

function materialOffset(
  material: pc.StandardMaterial,
  key: 'diffuseMapOffset' | 'emissiveMapOffset' | 'opacityMapOffset',
): pc.Vec2 {
  const offset = material[key];
  return new pc.Vec2(offset?.x ?? 0, offset?.y ?? 0);
}

function setMaterialOffset(
  material: pc.StandardMaterial,
  key: 'diffuseMapOffset' | 'emissiveMapOffset' | 'opacityMapOffset',
  u: number,
  v: number,
): void {
  const offset = material[key] ?? new pc.Vec2();
  offset.set(u, v);
  material[key] = offset;
}

function applyTextureScrollMaterialOffset(state: TextureScrollState): void {
  const delta = textureScrollOffset(state.scroll, state.elapsedSeconds);
  if (state.material.diffuseMap) {
    setMaterialOffset(
      state.material,
      'diffuseMapOffset',
      wrapTextureOffset(state.baseDiffuseOffset.x + delta.u),
      wrapTextureOffset(state.baseDiffuseOffset.y + delta.v),
    );
  }
  if (state.material.emissiveMap) {
    setMaterialOffset(
      state.material,
      'emissiveMapOffset',
      wrapTextureOffset(state.baseEmissiveOffset.x + delta.u),
      wrapTextureOffset(state.baseEmissiveOffset.y + delta.v),
    );
  }
  if (state.material.opacityMap) {
    setMaterialOffset(
      state.material,
      'opacityMapOffset',
      wrapTextureOffset(state.baseOpacityOffset.x + delta.u),
      wrapTextureOffset(state.baseOpacityOffset.y + delta.v),
    );
  }
  state.material.update();
}

function tagDrawOrder(entity: pc.Entity, order = WORLD_DRAW_ORDER): void {
  if (!entity.render) return;
  for (const mi of entity.render.meshInstances) {
    mi.drawOrder = order;
  }
}

function tagEntityTreeDrawOrder(root: pc.Entity, order = WORLD_DRAW_ORDER): void {
  tagDrawOrder(root, order);
  for (const child of root.children) {
    tagEntityTreeDrawOrder(child as pc.Entity, order);
  }
}

/** Ensure GLB panel meshes participate in the scene depth prepass. */
function tagEntityTreeMaterialDepth(root: pc.Entity): void {
  if (root.render) {
    for (const mi of root.render.meshInstances) {
      const mat = mi.material;
      if (mat instanceof pc.StandardMaterial) {
        const cutout = isAlphaCutoutMaterial(mat);
        const transparent = (Boolean(mat.opacityMap) && !cutout) || mat.opacity < 0.999;
        if (cutout) mat.alphaTest = alphaCutoutThreshold(mat);
        mat.blendType = cutout || !transparent ? pc.BLEND_NONE : pc.BLEND_NORMAL;
        mat.depthWrite = cutout || !transparent;
        mat.depthTest = true;
        mat.update();
      }
    }
  }
  for (const child of root.children) {
    tagEntityTreeMaterialDepth(child as pc.Entity);
  }
}

function createPlaneEntity(name: string, material: pc.Material): pc.Entity {
  const entity = new pc.Entity(name);
  entity.addComponent('render', {
    type: 'plane',
    castShadows: false,
    receiveShadows: false,
    material,
    layers: [pc.LAYERID_WORLD],
  });
  tagDrawOrder(entity);
  return entity;
}

function collectRenderEntities(root: pc.Entity, out: pc.Entity[] = []): pc.Entity[] {
  if (root.render) out.push(root);
  for (const child of root.children) {
    collectRenderEntities(child as pc.Entity, out);
  }
  return out;
}

function setEntityTreeEnabled(entity: pc.Entity, enabled: boolean): void {
  entity.enabled = enabled;
  for (const child of entity.children) {
    setEntityTreeEnabled(child as pc.Entity, enabled);
  }
}

function buildPickTrisFromEntity(entity: pc.Entity): pc.Tri[] {
  const tris: pc.Tri[] = [];
  if (!entity.render) return tris;

  const positions: number[] = [];
  const indices: number[] = [];
  const v0 = new pc.Vec3();
  const v1 = new pc.Vec3();
  const v2 = new pc.Vec3();

  for (const mi of entity.render.meshInstances) {
    const mesh = mi.mesh;
    if (!mesh) continue;
    positions.length = 0;
    indices.length = 0;
    mesh.getPositions(positions);
    mesh.getIndices(indices);
    for (let i = 0; i < indices.length; i += 3) {
      const a = indices[i]! * 3;
      const b = indices[i + 1]! * 3;
      const c = indices[i + 2]! * 3;
      v0.set(positions[a]!, positions[a + 1]!, positions[a + 2]!);
      v1.set(positions[b]!, positions[b + 1]!, positions[b + 2]!);
      v2.set(positions[c]!, positions[c + 1]!, positions[c + 2]!);
      tris.push(new pc.Tri(v0.clone(), v1.clone(), v2.clone()));
    }
  }
  return tris;
}

type RayOccluderKind = 'panel' | 'interactable' | 'label';

interface RayOccluder {
  kind: RayOccluderKind;
  entity: pc.Entity;
  tris: pc.Tri[];
  panel?: PanelState;
  interactable?: InteractableState;
  label?: LabelState;
}

interface LabelState {
  entity: pc.Entity;
  def: WorldLabel;
  material: pc.StandardMaterial;
  materialBaseline: MaterialBaseline;
  worldRevealFade?: { startMs: number; durationMs: number } | null;
  /** True when billboard is enabled (faces camera). */
  billboard: boolean;
}

interface MaterialBaseline {
  material: pc.StandardMaterial;
  opacity: number;
  emissiveIntensity: number;
  blendType: number;
  depthWrite: boolean;
  depthTest: boolean;
  alphaTest: number;
}

/** Ray vs mesh triangles in entity local space. Returns world-space distance² along ray. */
function raycastMeshTris(
  ray: pc.Ray,
  tris: pc.Tri[],
  worldTransform: pc.Mat4,
  invWorldTransform: pc.Mat4,
  localOrigin: pc.Vec3,
  localDir: pc.Vec3,
  localRay: pc.Ray,
  hitPt: pc.Vec3,
): number | null {
  if (tris.length === 0) return null;

  invWorldTransform.copy(worldTransform).invert();
  invWorldTransform.transformPoint(ray.origin, localOrigin);
  invWorldTransform.transformVector(ray.direction, localDir);
  localDir.normalize();
  localRay.origin.copy(localOrigin);
  localRay.direction.copy(localDir);

  let bestDistSq = Infinity;
  for (const tri of tris) {
    if (tri.intersectsRay(localRay, hitPt)) {
      const dx = hitPt.x - ray.origin.x;
      const dy = hitPt.y - ray.origin.y;
      const dz = hitPt.z - ray.origin.z;
      const dSq = dx * dx + dy * dy + dz * dz;
      if (dSq < bestDistSq) bestDistSq = dSq;
    }
  }
  return bestDistSq === Infinity ? null : bestDistSq;
}

function isOccluderActive(occ: RayOccluder): boolean {
  if (!occ.entity.enabled) return false;
  if (!occ.entity.render) return false;
  for (const mi of occ.entity.render.meshInstances) {
    if (!mi.visible) return false;
  }
  return true;
}

function registerEntityOccluders(
  out: RayOccluder[],
  root: pc.Entity,
  kind: RayOccluderKind,
  panel?: PanelState,
  interactable?: InteractableState,
  label?: LabelState,
): void {
  for (const node of collectRenderEntities(root)) {
    const tris = buildPickTrisFromEntity(node);
    if (tris.length === 0) continue;
    out.push({ kind, entity: node, tris, panel, interactable, label });
  }
}

export class PlayCanvasWorldContent {
  private readonly root: pc.Entity;
  private readonly panelStates: PanelState[] = [];
  private readonly panelRegistry = new Map<string, PanelState>();
  private readonly interactables: InteractableState[] = [];
  private readonly interactableById = new Map<string, InteractableState>();
  private readonly labels: LabelState[] = [];
  private readonly labelById = new Map<string, LabelState>();
  private readonly textureScrolls: TextureScrollState[] = [];
  private readonly assets: pc.Asset[] = [];
  private readonly audioPool: HTMLAudioElement[] = [];
  private zones: WorldZone[] = [];
  private readonly zoneActive = new Map<string, boolean>();
  private hoveredId: string | null = null;
  private tapTargetActive = false;
  /** Set when the hovered interactable changes; consumed by the render-on-demand loop. */
  private hoverDirty = false;
  /** Scratch sphere reused by the per-frame animating-content frustum test. */
  private readonly cullSphere = new pc.BoundingSphere();
  /** Throttle CPU hover raycasts — 10 Hz is imperceptible for cursor feedback but ~6x cheaper. */
  private lastHoverCheckMs = 0;
  private static readonly HOVER_INTERVAL_MS = 100;
  private readonly basisXDeg: number;
  private readonly rayOccluders: RayOccluder[] = [];
  private readonly pickRay = new pc.Ray();
  private readonly pickNear = new pc.Vec3();
  private readonly pickFar = new pc.Vec3();
  private readonly localPickRay = new pc.Ray();
  private readonly invWorldTransform = new pc.Mat4();
  private readonly localRayOrigin = new pc.Vec3();
  private readonly localRayDir = new pc.Vec3();
  private readonly pickHit = new pc.Vec3();
  private readonly worldTransform = new pc.Mat4();
  private readonly interactionContext: InteractionContext;
  private readonly syncLayout: (() => void) | null;
  private prewarmedForReveal = false;
  private worldContentVisible = false;

  constructor(
    private readonly app: pc.AppBase,
    private readonly device: pc.GraphicsDevice,
    playCanvasBasis: boolean,
    syncLayout?: () => void,
    private videoAutoplayEnabled = true,
  ) {
    this.syncLayout = syncLayout ?? null;
    this.root = new pc.Entity('world-content');
    this.root.enabled = false;
    app.root.addChild(this.root);
    this.basisXDeg = playCanvasBasis ? 180 : 0;

    this.interactionContext = {
      showPanel: (id) => {
        const state = this.panelRegistry.get(id);
        if (!state) return;
        state.intendedVisible = true;
        this.syncPanelVisibility(state);
      },
      hidePanel: (id) => {
        const state = this.panelRegistry.get(id);
        if (!state) return;
        state.intendedVisible = false;
        state.videoFadeAnim = null;
        state.video?.pause();
        this.syncPanelVisibility(state);
      },
      togglePanel: (id) => {
        const state = this.panelRegistry.get(id);
        if (!state) return;
        if (this.isPanelShowing(state)) this.interactionContext.hidePanel(id);
        else this.interactionContext.showPanel(id);
      },
      playSound: (url) => {
        const audio = new Audio(url);
        this.audioPool.push(audio);
        void audio.play().catch(() => {});
      },
      playPanelVideo: (id) => {
        const state = this.panelRegistry.get(id);
        if (state?.video) this.playPanelVideoState(state);
      },
      pausePanelVideo: (id) => {
        const state = this.panelRegistry.get(id);
        if (state?.video) this.pausePanelVideoForClickToPlay(state);
      },
    };
  }

  get interactionCtx(): InteractionContext {
    return this.interactionContext;
  }

  setVideoAutoplay(enabled: boolean): void {
    if (this.videoAutoplayEnabled === enabled) return;
    this.videoAutoplayEnabled = enabled;
    for (const state of this.panelStates) {
      if (!state.video) continue;
      if (!enabled) {
        this.pausePanelVideoForClickToPlay(state);
      } else if (state.videoAutoplayAllowed !== false) {
        this.playPanelVideoState(state);
      }
    }
  }

  runAction(code: string): void {
    evalHandler(code, this.interactionContext);
  }

  async load(scene: LoadedScene): Promise<void> {
    this.clear();

    for (const panel of scene.panels ?? []) {
      try {
        await this.loadPanel(panel);
      } catch (err) {
        const label = panel.id ?? panel.type ?? 'panel';
        console.warn(`[PlayCanvasWorld] panel "${label}" failed:`, err);
      }
    }

    for (const def of scene.interactables ?? []) {
      try {
        await this.loadInteractable(def);
      } catch (err) {
        console.warn(`[PlayCanvasWorld] interactable "${def.id}" failed:`, err);
      }
    }

    for (const def of scene.labels ?? []) {
      try {
        await this.loadLabel(def);
      } catch (err) {
        console.warn(`[PlayCanvasWorld] label "${def.id}" failed:`, err);
      }
    }

    console.info(
      `[PlayCanvasWorld] loaded ${this.panelStates.length} panels, ${this.interactables.length} interactables, ${this.labels.length} labels`,
    );

    this.zones = scene.zones ?? [];
    this.zoneActive.clear();
    for (const zone of this.zones) {
      this.zoneActive.set(zone.id, false);
    }
  }

  private async loadLabel(def: WorldLabel): Promise<void> {
    const scale =
      typeof def.scale === 'number' && Number.isFinite(def.scale) && def.scale > 0 ? def.scale : 1;
    const rotation = def.rotation ?? [0, 0, 0];
    const billboard = (def.billboard ?? 'camera') === 'camera';

    const canvas = document.createElement('canvas');
    canvas.width = LABEL_TEX_SIZE;
    canvas.height = LABEL_TEX_SIZE;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('2d canvas unavailable');

    // Background
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = def.color ?? '#0d1e3a';
    ctx.globalAlpha = 0.9;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.globalAlpha = 1;

    // Text
    ctx.fillStyle = def.textColor ?? '#ffffff';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.font = `700 ${Math.round(LABEL_TEX_SIZE * 0.09)}px system-ui, -apple-system, Segoe UI, Roboto, Arial`;

    // Basic word wrap (few lines, centered).
    const maxWidth = LABEL_TEX_SIZE * 0.86;
    const words = def.text.split(/\s+/).filter(Boolean);
    const lines: string[] = [];
    let current = '';
    for (const w of words) {
      const next = current ? `${current} ${w}` : w;
      if (ctx.measureText(next).width <= maxWidth || !current) current = next;
      else {
        lines.push(current);
        current = w;
      }
    }
    if (current) lines.push(current);
    const maxLines = 5;
    const trimmed = lines.slice(0, maxLines);
    const lineH = LABEL_TEX_SIZE * 0.12;
    const startY = LABEL_TEX_SIZE * 0.5 - ((trimmed.length - 1) * lineH) / 2;
    for (let i = 0; i < trimmed.length; i++) {
      ctx.fillText(trimmed[i]!, LABEL_TEX_SIZE * 0.5, startY + i * lineH);
    }

    const texture = textureFromCanvas(this.device, canvas);
    texture.minFilter = pc.FILTER_LINEAR_MIPMAP_LINEAR;
    texture.magFilter = pc.FILTER_LINEAR;

    const material = new pc.StandardMaterial();
    material.diffuseMap = texture;
    material.emissiveMap = texture;
    material.emissiveIntensity = 0.35;
    material.opacity = 1;
    material.blendType = pc.BLEND_NORMAL;
    material.depthWrite = true;
    material.update();

    const label = new pc.Entity(`label-${def.id}`);
    label.setLocalPosition(def.position[0], def.position[1], def.position[2]);
    label.setLocalEulerAngles(rotation[0], rotation[1], rotation[2]);
    label.setLocalScale(scale, scale, scale);

    label.addComponent('render', {
      type: 'plane',
      castShadows: false,
      receiveShadows: false,
      material,
    });

    // Authoring-friendly size defaults (world units).
    label.setLocalScale(1.2 * scale, 0.35 * scale, 1 * scale);

    this.root.addChild(label);

    const state: LabelState = {
      entity: label,
      def,
      material,
      materialBaseline: captureMaterialBaseline(material),
      billboard,
    };
    this.labels.push(state);
    this.labelById.set(def.id, state);

    // Labels are pickable if onClick exists; include them in occluders so they can be hit.
    if (typeof def.onClick === 'string' && def.onClick.length > 0) {
      registerEntityOccluders(this.rayOccluders, label, 'label', undefined, undefined, state);
    }
  }

  private async loadPanel(panel: WorldPanel): Promise<void> {
    if (panel.type === 'web' || panel.type === 'videoSound' || panel.type === 'htmlCanvas') {
      console.warn(`[PlayCanvasWorld] panel type "${panel.type}" is not supported in native mode`);
      return;
    }

    const emissiveRaw = (
      panel as SimplePanelDef | TexturedPanelDef | VideoPanelDef | VideoModelPanelDef
    ).emissiveIntensity;
    const emissive = resolvePanelEmissive(emissiveRaw);
    const baseOpacity = panel.opacity ?? 1;
    const proximity = resolvePanelProximity(panel);
    const alphaFromTexture =
      panel.type === 'textured' && (panel as TexturedPanelDef).alphaFromTexture === true;
    const alphaCutout =
      panel.type === 'textured' ? (panel as TexturedPanelDef).alphaCutout : undefined;
    const repeatTexture = hasTextureScroll(panel.uvScroll);
    const intendedVisible = panel.visible ?? true;
    const videoFadeInMs =
      panel.type === 'video'
        ? ((panel as VideoPanelDef).videoFadeInMs ?? 0)
        : panel.type === 'videoModel'
          ? ((panel as VideoModelPanelDef).videoFadeInMs ?? 0)
          : 0;

    if (panel.type === 'model') {
      await this.loadModelPanel(panel as ModelPanelDef, intendedVisible);
      return;
    }

    if (panel.type === 'videoModel') {
      await this.loadVideoModelPanel(panel as VideoModelPanelDef, intendedVisible);
      return;
    }

    let diffuse: pc.Texture | null = null;
    let video: HTMLVideoElement | undefined;
    let videoTexture: pc.Texture | undefined;

    if (!panel.type || panel.type === 'simple') {
      const sp = panel as SimplePanelDef;
      const canvas = makePanelCanvas(sp.color ?? '#888888', sp.text, sp.textColor);
      diffuse = textureFromCanvas(this.device, canvas);
    } else if (panel.type === 'textured') {
      const tp = panel as TexturedPanelDef;
      const asset = await loadAsset(
        this.app,
        `tex-${panel.id ?? tp.textureUrl}`,
        'texture',
        tp.textureUrl,
      );
      this.assets.push(asset);
      diffuse = asset.resource as pc.Texture;
    } else if (panel.type === 'video') {
      const vp = panel as VideoPanelDef;
      const videoAutoplayAllowed = vp.autoplay !== false;
      const autoplay = shouldPanelVideoAutoplay(vp.autoplay, this.videoAutoplayEnabled);
      video = document.createElement('video');
      video.src = vp.videoUrl;
      video.muted = true;
      video.loop = true;
      video.playsInline = true;
      video.crossOrigin = 'anonymous';
      await new Promise<void>((resolve) => {
        const done = () => resolve();
        if (video!.readyState >= 2) {
          done();
          return;
        }
        video!.addEventListener('loadeddata', done, { once: true });
        video!.addEventListener('error', done, { once: true });
        if (autoplay) void video!.play().catch(() => {});
        else void video!.load();
      });
      videoTexture = createVideoTexture(this.device, video);
      const videoScratchCanvas = autoplay
        ? uploadVideoTexture(this.device, videoTexture, video)
        : uploadVideoPlaceholderTexture(videoTexture, video);
      diffuse = videoTexture;

      const initialOpacity = videoFadeInMs > 0 && !intendedVisible ? 0 : baseOpacity;
      const material = createPlaneMaterial(diffuse, emissive, initialOpacity, {
        repeatTexture,
      });
      const entity = createPlaneEntity(panel.id ?? 'panel', material);
      applyTransform(entity, panel, this.basisXDeg);
      tagDrawOrder(entity);
      entity.enabled = false;
      this.root.addChild(entity);

      const state: PanelState = {
        entity,
        intendedVisible,
        video,
        videoTexture,
        material,
        materials: [material],
        baseOpacity,
        baseEmissive: emissive,
        videoFadeInMs,
        videoFadeAnim: null,
        videoScratchCanvas,
        videoFrameDirty: autoplay,
        videoAutoplayAllowed,
        videoClickToPlay: !autoplay,
        worldRevealAlpha: 1,
        ...proximity,
      };
      this.registerTextureScroll(entity, material, panel.uvScroll);
      state.videoFrameCancel = watchVideoFrames(video, () => {
        state.videoFrameDirty = true;
      });
      this.panelStates.push(state);
      if (panel.id) this.panelRegistry.set(panel.id, state);
      registerEntityOccluders(this.rayOccluders, entity, 'panel', state);
      return;
    }

    const initialOpacity = videoFadeInMs > 0 && !intendedVisible ? 0 : baseOpacity;
    const material = createPlaneMaterial(diffuse, emissive, initialOpacity, {
      alphaFromTexture,
      alphaCutout,
      repeatTexture,
    });
    const entity = createPlaneEntity(panel.id ?? 'panel', material);
    applyTransform(entity, panel, this.basisXDeg);
    tagDrawOrder(entity);
    entity.enabled = false;
    this.root.addChild(entity);

    const state: PanelState = {
      entity,
      intendedVisible,
      video,
      videoTexture,
      material,
      materials: [material],
      baseOpacity,
      baseEmissive: emissive,
      videoFadeInMs,
      videoFadeAnim: null,
      worldRevealAlpha: 1,
      ...proximity,
    };
    this.registerTextureScroll(entity, material, panel.uvScroll);
    this.panelStates.push(state);
    if (panel.id) this.panelRegistry.set(panel.id, state);
    registerEntityOccluders(this.rayOccluders, entity, 'panel');
  }

  private async loadModelPanel(panel: ModelPanelDef, intendedVisible: boolean): Promise<void> {
    const asset = await loadAsset(
      this.app,
      `model-${panel.id ?? panel.modelUrl}`,
      'container',
      panel.modelUrl,
    );
    this.assets.push(asset);
    const resource = asset.resource as pc.ContainerResource & { animations?: pc.Asset[] };
    const entity = resource.instantiateRenderEntity({ castShadows: false, receiveShadows: false });
    applyModelTransform(entity, panel, this.basisXDeg, panel.scale ?? 1);
    const materials = ownEntityTreeStandardMaterials(entity);
    if (panel.textureUrl) {
      const textureAsset = await loadAsset(
        this.app,
        `model-tex-${panel.id ?? panel.textureUrl}`,
        'texture',
        panel.textureUrl,
      );
      this.assets.push(textureAsset);
      applyModelTextureOverride(
        materials,
        textureAsset.resource as pc.Texture,
        panel.emissiveIntensity ?? 1,
        panel.opacity ?? 1,
        {
          alphaFromTexture: panel.alphaFromTexture === true,
          alphaCutout: panel.alphaCutout,
          repeatTexture: hasTextureScroll(panel.uvScroll),
        },
      );
    }
    tagEntityTreeDrawOrder(entity);
    for (const material of materials) {
      this.registerTextureScroll(entity, material, panel.uvScroll);
    }
    tagEntityTreeMaterialDepth(entity);
    setEntityTreeEnabled(entity, false);
    this.root.addChild(entity);

    const animPlayState = setupModelAnimations(entity, resource.animations ?? [], {
      animation: panel.animation,
      autoplay: panel.autoplay,
      loop: panel.loop,
    });

    const state: PanelState = {
      entity,
      intendedVisible,
      baseOpacity: panel.opacity ?? 1,
      baseEmissive: resolvePanelEmissive(panel.emissiveIntensity),
      videoFadeInMs: 0,
      videoFadeAnim: null,
      worldRevealAlpha: 1,
      ...resolvePanelProximity(panel),
      animPlayState,
      materials,
      materialBaselines: materials.map(captureMaterialBaseline),
    };
    this.panelStates.push(state);
    if (panel.id) this.panelRegistry.set(panel.id, state);
    registerEntityOccluders(this.rayOccluders, entity, 'panel');
  }

  private async loadVideoModelPanel(
    panel: VideoModelPanelDef,
    intendedVisible: boolean,
  ): Promise<void> {
    const asset = await loadAsset(
      this.app,
      `vmodel-${panel.id ?? panel.modelUrl}`,
      'container',
      panel.modelUrl,
    );
    this.assets.push(asset);
    const resource = asset.resource as pc.ContainerResource;
    const entity = resource.instantiateRenderEntity({ castShadows: false, receiveShadows: false });
    applyModelTransform(entity, panel, this.basisXDeg, panel.scale ?? 1);

    const videoAutoplayAllowed = panel.autoplay !== false;
    const autoplay = shouldPanelVideoAutoplay(panel.autoplay, this.videoAutoplayEnabled);
    const video = document.createElement('video');
    video.src = panel.videoUrl;
    video.muted = true;
    video.loop = true;
    video.playsInline = true;
    video.crossOrigin = 'anonymous';
    await new Promise<void>((resolve) => {
      const done = () => resolve();
      video.addEventListener('loadeddata', done, { once: true });
      video.addEventListener('error', done, { once: true });
      if (autoplay) void video.play().catch(() => {});
      else void video.load();
    });

    const videoTexture = createVideoTexture(this.device, video);
    const videoScratchCanvas = autoplay
      ? uploadVideoTexture(this.device, videoTexture, video)
      : uploadVideoPlaceholderTexture(videoTexture, video);

    const materials: pc.StandardMaterial[] = [];
    for (const node of collectRenderEntities(entity)) {
      if (!node.render) continue;
      for (const mi of node.render.meshInstances) {
        const mat = createPlaneMaterial(
          videoTexture,
          resolvePanelEmissive(panel.emissiveIntensity),
          1,
          {
            repeatTexture: hasTextureScroll(panel.uvScroll),
          },
        );
        materials.push(mat);
        mi.material = mat;
        mi.drawOrder = WORLD_DRAW_ORDER;
        this.registerTextureScroll(entity, mat, panel.uvScroll);
      }
    }

    tagEntityTreeDrawOrder(entity);
    tagEntityTreeMaterialDepth(entity);
    setEntityTreeEnabled(entity, false);
    this.root.addChild(entity);

    const state: PanelState = {
      entity,
      intendedVisible,
      video,
      videoTexture,
      material: undefined,
      materials,
      baseOpacity: 1,
      baseEmissive: resolvePanelEmissive(panel.emissiveIntensity),
      videoFadeInMs: panel.videoFadeInMs ?? 0,
      videoFadeAnim: null,
      videoScratchCanvas,
      videoFrameDirty: autoplay,
      videoAutoplayAllowed,
      videoClickToPlay: !autoplay,
      worldRevealAlpha: 1,
      ...resolvePanelProximity(panel),
    };
    state.videoFrameCancel = watchVideoFrames(video, () => {
      state.videoFrameDirty = true;
    });
    this.panelStates.push(state);
    if (panel.id) this.panelRegistry.set(panel.id, state);
    registerEntityOccluders(this.rayOccluders, entity, 'panel', state);
  }

  private async loadInteractable(def: WorldInteractable): Promise<void> {
    const iw = def.width ?? 0.8;
    const ih = def.height ?? 0.25;
    const normalCanvas = makePanelCanvas(def.color ?? '#1a3a5c', def.label, def.labelColor);
    const hoverCanvas = makePanelCanvas(def.hoverColor ?? '#3a7acc', def.label, def.labelColor);
    const normalTex = textureFromCanvas(this.device, normalCanvas);
    const hoverTex = textureFromCanvas(this.device, hoverCanvas);
    const normalMaterial = createPlaneMaterial(normalTex, 1, 1);
    const hoverMaterial = createPlaneMaterial(hoverTex, 1, 1);

    const entity = createPlaneEntity(`int-${def.id}`, normalMaterial);
    applyTransform(
      entity,
      {
        position: def.position,
        rotation: def.rotation,
        width: iw,
        height: ih,
      },
      this.basisXDeg,
    );
    entity.enabled = false;
    this.root.addChild(entity);

    const state: InteractableState = {
      entity,
      def,
      normalMaterial,
      hoverMaterial,
      normalBaseline: captureMaterialBaseline(normalMaterial),
      hoverBaseline: captureMaterialBaseline(hoverMaterial),
    };
    registerEntityOccluders(this.rayOccluders, entity, 'interactable', undefined, state);
    this.interactables.push(state);
    this.interactableById.set(def.id, state);
  }

  private isPanelShowing(state: PanelState): boolean {
    return this.root.enabled && state.intendedVisible;
  }

  private playPanelVideoState(state: PanelState): void {
    if (!state.video) return;
    state.videoClickToPlay = false;
    state.videoFrameDirty = true;
    if (state.videoFadeInMs > 0) {
      forEachPanelMaterial(state, (material) => applyPanelMaterialOpacity(material, 0, 0));
    }
    state.videoFadeAnim =
      state.videoFadeInMs > 0
        ? { startMs: performance.now(), durationMs: state.videoFadeInMs }
        : null;
    void state.video.play().catch(() => {
      this.pausePanelVideoForClickToPlay(state);
    });
  }

  private pausePanelVideoForClickToPlay(state: PanelState): void {
    if (!state.video || !state.videoTexture) return;
    state.video.pause();
    state.videoClickToPlay = true;
    state.videoFrameDirty = false;
    state.videoFadeAnim = null;
    state.videoScratchCanvas = uploadVideoPlaceholderTexture(
      state.videoTexture,
      state.video,
      state.videoScratchCanvas,
    );
    forEachPanelMaterial(state, (material) => material.update());
    this.hoverDirty = true;
  }

  private registerTextureScroll(
    entity: pc.Entity,
    material: pc.StandardMaterial,
    scroll: TextureScrollDef | undefined,
  ): void {
    if (!hasTextureScroll(scroll)) return;
    setTextureRepeat(material.diffuseMap);
    setTextureRepeat(material.emissiveMap);
    setTextureRepeat(material.opacityMap);
    const rotation =
      typeof scroll.rotation === 'number' && Number.isFinite(scroll.rotation) ? scroll.rotation : 0;
    material.diffuseMapRotation = rotation;
    material.emissiveMapRotation = rotation;
    material.opacityMapRotation = rotation;
    material.update();
    this.textureScrolls.push({
      entity,
      material,
      scroll,
      elapsedSeconds: 0,
      baseDiffuseOffset: materialOffset(material, 'diffuseMapOffset'),
      baseEmissiveOffset: materialOffset(material, 'emissiveMapOffset'),
      baseOpacityOffset: materialOffset(material, 'opacityMapOffset'),
    });
  }

  private registerEntityTextureScrolls(
    entity: pc.Entity,
    scroll: TextureScrollDef | undefined,
  ): void {
    if (!hasTextureScroll(scroll)) return;
    for (const node of collectRenderEntities(entity)) {
      if (!node.render) continue;
      for (const mi of node.render.meshInstances) {
        if (!(mi.material instanceof pc.StandardMaterial)) continue;
        const material = mi.material.clone() as pc.StandardMaterial;
        mi.material = material;
        this.registerTextureScroll(entity, material, scroll);
      }
    }
  }

  private syncPanelVisibility(state: PanelState): void {
    this.applyPanelVisibility(state, this.isPanelShowing(state));
  }

  private ensurePanelMaterialBaselines(state: PanelState): MaterialBaseline[] {
    if (!state.materialBaselines) {
      const materials = state.materials ?? collectUniqueStandardMaterials(state.entity);
      state.materialBaselines = materials.map(captureMaterialBaseline);
    }
    return state.materialBaselines;
  }

  private applyPanelRevealAlpha(state: PanelState, alpha: number): void {
    state.worldRevealAlpha = alpha;
    for (const baseline of this.ensurePanelMaterialBaselines(state)) {
      applyMaterialBaselineAlpha(baseline, state.worldRevealAlpha * state.proximityAlpha);
    }
  }

  private applyPanelProximityAlpha(state: PanelState, alpha: number): void {
    state.proximityAlpha = alpha;
    for (const baseline of this.ensurePanelMaterialBaselines(state)) {
      applyMaterialBaselineAlpha(baseline, state.worldRevealAlpha * state.proximityAlpha);
    }
  }

  private applyInteractableRevealAlpha(state: InteractableState, alpha: number): void {
    applyMaterialBaselineAlpha(state.normalBaseline, alpha);
    applyMaterialBaselineAlpha(state.hoverBaseline, alpha);
  }

  private applyLabelRevealAlpha(state: LabelState, alpha: number): void {
    applyMaterialBaselineAlpha(state.materialBaseline, alpha);
  }

  private applyPanelVisibility(state: PanelState, visible: boolean): void {
    const proximityVisible =
      state.proximityRevealDistance === undefined || state.proximityAlpha > 0.001;
    const enabled = visible && proximityVisible;
    setEntityTreeEnabled(state.entity, enabled);
    if (enabled && state.animPlayState && state.entity.anim) {
      state.entity.anim.rebind();
      state.entity.anim.baseLayer?.play(state.animPlayState);
      state.entity.anim.playing = true;
    }
    for (const node of collectRenderEntities(state.entity)) {
      if (!node.render) continue;
      for (const mi of node.render.meshInstances) {
        mi.visible = enabled;
      }
    }
    forEachPanelMaterial(state, (material) => material.update());
  }

  prewarmForReveal(): void {
    if (this.prewarmedForReveal) return;
    this.prewarmedForReveal = true;
    this.root.enabled = true;

    for (const state of this.panelStates) {
      const show = state.intendedVisible;
      this.applyPanelVisibility(state, show);
      state.worldRevealFade = null;
      if (show) {
        this.ensurePanelMaterialBaselines(state);
        forEachPanelMaterial(state, (material) => applyPanelMaterialOpacity(material, 0, 0));
        this.applyPanelRevealAlpha(state, 0);
        if (state.video) state.videoFrameDirty = true;
      }
    }

    for (const item of this.interactables) {
      setEntityTreeEnabled(item.entity, true);
      if (item.entity.render) {
        for (const mi of item.entity.render.meshInstances) {
          mi.material = item.normalMaterial;
          mi.visible = true;
        }
      }
      item.worldRevealFade = null;
      this.applyInteractableRevealAlpha(item, 0);
    }

    for (const label of this.labels) {
      setEntityTreeEnabled(label.entity, true);
      label.worldRevealFade = null;
      this.applyLabelRevealAlpha(label, 0);
    }

    this.hoveredId = null;
    this.tapTargetActive = false;
    this.resize();
  }

  setWorldContentVisible(worldVisible: boolean, fadeIn = false): void {
    const fade = worldVisible && (fadeIn || this.prewarmedForReveal);
    this.prewarmedForReveal = false;
    this.worldContentVisible = worldVisible;
    this.root.enabled = worldVisible;
    for (const state of this.panelStates) {
      const show = worldVisible && state.intendedVisible;
      this.applyPanelVisibility(state, show);
      if (fade && show) {
        state.worldRevealFade = { startMs: performance.now(), durationMs: 1100 };
        this.applyPanelRevealAlpha(state, 0);
      } else {
        state.worldRevealFade = null;
        if (show) this.applyPanelRevealAlpha(state, 1);
      }
    }
    for (const item of this.interactables) {
      setEntityTreeEnabled(item.entity, worldVisible);
      if (worldVisible && item.entity.render) {
        for (const mi of item.entity.render.meshInstances) {
          mi.material = item.normalMaterial;
          mi.visible = true;
        }
      }
      if (fade && worldVisible) {
        item.worldRevealFade = { startMs: performance.now(), durationMs: 1100 };
        this.applyInteractableRevealAlpha(item, 0);
      } else {
        item.worldRevealFade = null;
        this.applyInteractableRevealAlpha(item, worldVisible ? 1 : 0);
      }
    }
    for (const label of this.labels) {
      setEntityTreeEnabled(label.entity, worldVisible);
      if (fade && worldVisible) {
        label.worldRevealFade = { startMs: performance.now(), durationMs: 1100 };
        this.applyLabelRevealAlpha(label, 0);
      } else {
        label.worldRevealFade = null;
        this.applyLabelRevealAlpha(label, worldVisible ? 1 : 0);
      }
    }
    this.hoveredId = null;
    this.tapTargetActive = false;
    if (worldVisible) this.resize();
  }

  tick(dt: number, cameraPos: pc.Vec3, uploadVideoFrames = true, updateZones = true): void {
    const now = performance.now();

    for (const state of this.textureScrolls) {
      if (!state.entity.enabled) continue;
      state.elapsedSeconds += dt;
      if (uploadVideoFrames) applyTextureScrollMaterialOffset(state);
    }

    for (const state of this.panelStates) {
      if (state.proximityRevealDistance !== undefined) {
        const alpha = panelProximityAlpha(state, cameraPos);
        if (Math.abs(alpha - state.proximityAlpha) > 0.001) {
          const wasEnabledByProximity = state.proximityAlpha > 0.001;
          this.applyPanelProximityAlpha(state, alpha);
          if (wasEnabledByProximity !== alpha > 0.001) {
            this.applyPanelVisibility(state, this.worldContentVisible && state.intendedVisible);
          }
        }
      }
      // Only re-upload when the video advanced a frame (rVFC sets the dirty flag).
      // Without rVFC, videoFrameDirty stays true and we upload every tick as before.
      if (
        uploadVideoFrames &&
        state.videoTexture &&
        state.video &&
        state.entity.enabled &&
        !state.videoClickToPlay &&
        state.videoFrameDirty !== false
      ) {
        state.videoScratchCanvas = uploadVideoTexture(
          this.device,
          state.videoTexture,
          state.video,
          state.videoScratchCanvas,
        );
        if (state.videoFrameCancel) state.videoFrameDirty = false;
      }
      if (state.videoFadeAnim) {
        const t = Math.min(1, (now - state.videoFadeAnim.startMs) / state.videoFadeAnim.durationMs);
        forEachPanelMaterial(state, (material) =>
          applyPanelMaterialOpacity(material, state.baseOpacity * t, state.baseEmissive * t),
        );
        if (t >= 1) state.videoFadeAnim = null;
      }
      if (state.worldRevealFade) {
        const t = Math.min(
          1,
          (now - state.worldRevealFade.startMs) / state.worldRevealFade.durationMs,
        );
        const e = t * t * (3 - 2 * t);
        this.applyPanelRevealAlpha(state, e);
        if (t >= 1) state.worldRevealFade = null;
      }
    }

    for (const item of this.interactables) {
      if (!item.worldRevealFade) continue;
      const t = Math.min(
        1,
        (now - item.worldRevealFade.startMs) / item.worldRevealFade.durationMs,
      );
      const e = t * t * (3 - 2 * t);
      this.applyInteractableRevealAlpha(item, e);
      if (t >= 1) item.worldRevealFade = null;
    }

    for (const label of this.labels) {
      if (!label.worldRevealFade) continue;
      const t = Math.min(
        1,
        (now - label.worldRevealFade.startMs) / label.worldRevealFade.durationMs,
      );
      const e = t * t * (3 - 2 * t);
      this.applyLabelRevealAlpha(label, e);
      if (t >= 1) label.worldRevealFade = null;
    }

    if (updateZones) {
      for (const zone of this.zones) {
        const [cx, , cz] = zone.center;
        const dx = cameraPos.x - cx;
        const dz = cameraPos.z - cz;
        const inside = dx * dx + dz * dz < zone.radius * zone.radius;
        const wasInside = this.zoneActive.get(zone.id) ?? false;
        if (inside !== wasInside) {
          this.zoneActive.set(zone.id, inside);
          const code = inside ? zone.onEnter : zone.onLeave;
          if (code) evalHandler(code, this.interactionContext);
        }
      }
    }
  }

  fireInteract(camera: pc.Entity): void {
    this.fireInteractAt(0, 0, camera);
  }

  /**
   * True while any content is animating (a playing video, an active fade, or a playing
   * model animation), regardless of visibility. Cheap pre-check for the render-on-demand
   * loop — if false there is no need to build a frustum and test visibility.
   */
  hasAnimatingContent(): boolean {
    for (const state of this.textureScrolls) {
      if (state.entity.enabled) return true;
    }
    for (const state of this.panelStates) {
      if (!state.entity.enabled) continue;
      if (state.videoFadeAnim) return true;
      if (state.worldRevealFade) return true;
      if (panelVideoNeedsRender(state)) return true;
      if (state.entity.anim?.playing) return true;
    }
    for (const item of this.interactables) {
      if (item.entity.enabled && item.entity.anim?.playing) return true;
      if (item.entity.enabled && item.worldRevealFade) return true;
    }
    for (const label of this.labels) {
      if (label.entity.enabled && label.worldRevealFade) return true;
    }
    return false;
  }

  /**
   * True while animating content is actually inside the given camera frustum. Lets the
   * render-on-demand loop stay fully idle when the only thing moving (e.g. a looping logo
   * or a playing video) is off-screen — turning to face it wakes rendering again. The anim
   * components keep advancing on `app.update` regardless, so poses stay correct off-screen.
   */
  hasVisibleAnimatingContent(frustum: pc.Frustum): boolean {
    for (const state of this.textureScrolls) {
      if (state.entity.enabled && this.entityInFrustum(state.entity, frustum)) return true;
    }
    for (const state of this.panelStates) {
      if (!state.entity.enabled) continue;
      const animating =
        !!state.videoFadeAnim ||
        !!state.worldRevealFade ||
        panelVideoNeedsRender(state) ||
        !!state.entity.anim?.playing;
      if (animating && this.entityInFrustum(state.entity, frustum)) return true;
    }
    for (const item of this.interactables) {
      if (
        item.entity.enabled &&
        (item.entity.anim?.playing || item.worldRevealFade) &&
        this.entityInFrustum(item.entity, frustum)
      ) {
        return true;
      }
    }
    for (const label of this.labels) {
      if (
        label.entity.enabled &&
        label.worldRevealFade &&
        this.entityInFrustum(label.entity, frustum)
      ) {
        return true;
      }
    }
    return false;
  }

  /** Frustum test over an entity subtree's render mesh instances (world-space bounds). */
  private entityInFrustum(entity: pc.Entity, frustum: pc.Frustum): boolean {
    const renders = entity.findComponents('render') as pc.RenderComponent[];
    let tested = false;
    for (const render of renders) {
      for (const mi of render.meshInstances) {
        tested = true;
        this.cullSphere.center.copy(mi.aabb.center);
        this.cullSphere.radius = mi.aabb.halfExtents.length() * 1.1; // small pad for anim sway
        if (frustum.containsSphere(this.cullSphere) > 0) return true;
      }
    }
    // No render geometry to test against — err on the side of drawing.
    return !tested;
  }

  /** One-shot: returns (and clears) whether the hovered interactable changed since last call. */
  consumeHoverDirty(): boolean {
    const dirty = this.hoverDirty;
    this.hoverDirty = false;
    return dirty;
  }

  hasTapTarget(): boolean {
    return this.tapTargetActive;
  }

  updateInteractableHover(camera: pc.Entity, nowMs = performance.now(), force = false): void {
    if (!force && nowMs - this.lastHoverCheckMs < PlayCanvasWorldContent.HOVER_INTERVAL_MS) return;
    this.lastHoverCheckMs = nowMs;
    const meshResult = this.raycastMeshesAt(camera, 0, 0);
    const hit = meshResult?.hit ?? null;
    const hitInteractable =
      hit?.kind === 'interactable' && hit.interactable?.def.onClick ? hit.interactable : null;
    const hitLabel = hit?.kind === 'label' && hit.label?.def.onClick ? hit.label : null;
    const hitVideoPanel =
      hit?.kind === 'panel' && hit.panel?.video && hit.panel.videoClickToPlay ? hit.panel : null;
    const hitId = hitInteractable?.def.id ?? null;
    const tapTargetActive = Boolean(hitInteractable || hitLabel || hitVideoPanel);
    if (hitId === this.hoveredId && tapTargetActive === this.tapTargetActive) return;
    this.hoverDirty = true;
    this.tapTargetActive = tapTargetActive;

    if (this.hoveredId) {
      const old = this.interactableById.get(this.hoveredId);
      if (old?.entity.render) {
        old.entity.render.meshInstances.forEach((mi) => {
          mi.material = old.normalMaterial;
          old.normalMaterial.update();
        });
      }
    }
    if (hitId) {
      const next = this.interactableById.get(hitId);
      if (next?.entity.render) {
        next.entity.render.meshInstances.forEach((mi) => {
          mi.material = next.hoverMaterial;
          next.hoverMaterial.update();
        });
      }
    }
    this.hoveredId = hitId;
  }

  fireInteractAt(ndcX: number, ndcY: number, camera: pc.Entity): void {
    const meshResult = this.raycastMeshesAt(camera, ndcX, ndcY);
    if (!meshResult) return;
    const hit = meshResult.hit;
    if (hit.kind === 'interactable' && hit.interactable?.def.onClick) {
      evalHandler(hit.interactable.def.onClick, this.interactionContext);
      this.hoverDirty = true;
      return;
    }
    if (hit.kind === 'label' && hit.label?.def.onClick) {
      evalHandler(hit.label.def.onClick, this.interactionContext);
      this.hoverDirty = true;
      return;
    }
    if (hit.kind === 'panel' && hit.panel?.video && hit.panel.videoClickToPlay) {
      this.playPanelVideoState(hit.panel);
      this.hoverDirty = true;
      return;
    }
  }

  resize(): void {
    this.syncLayout?.();
    this.app.root.syncHierarchy();
  }

  /** Per-frame: billboard labels (camera-facing) without touching overlay UI. */
  updateLabels(camera: pc.Entity): void {
    if (this.labels.length === 0) return;
    const camPos = camera.getPosition();
    for (const label of this.labels) {
      if (!label.billboard) continue;
      if (!label.entity.enabled) continue;
      // Face the camera; keep authored roll at 0 (UI-like sign).
      label.entity.lookAt(camPos);
      const e = label.entity.getLocalEulerAngles();
      label.entity.setLocalEulerAngles(0, e.y, 0);
    }
  }

  private raycastMeshesAt(
    camera: pc.Entity,
    ndcX: number,
    ndcY: number,
  ): { hit: RayOccluder; distSq: number } | null {
    if (!this.buildPickRay(camera, ndcX, ndcY)) return null;

    let best: RayOccluder | null = null;
    let bestDistSq = Infinity;

    for (const occ of this.rayOccluders) {
      if (!isOccluderActive(occ)) continue;
      const distSq = raycastMeshTris(
        this.pickRay,
        occ.tris,
        this.worldTransform.copy(occ.entity.getWorldTransform()),
        this.invWorldTransform,
        this.localRayOrigin,
        this.localRayDir,
        this.localPickRay,
        this.pickHit,
      );
      if (distSq !== null && distSq < bestDistSq) {
        bestDistSq = distSq;
        best = occ;
      }
    }
    return best ? { hit: best, distSq: bestDistSq } : null;
  }

  private buildPickRay(camera: pc.Entity, ndcX: number, ndcY: number): boolean {
    const cam = camera.camera;
    if (!cam) return false;

    this.device.updateClientRect();
    const w = this.device.clientRect.width;
    const h = this.device.clientRect.height;
    if (w <= 0 || h <= 0) return false;

    const screenX = (ndcX * 0.5 + 0.5) * w;
    const screenY = (1 - (ndcY * 0.5 + 0.5)) * h;

    cam.screenToWorld(screenX, screenY, cam.nearClip, this.pickNear);
    cam.screenToWorld(screenX, screenY, cam.farClip, this.pickFar);
    this.pickRay.origin.copy(this.pickNear);
    this.pickRay.direction.sub2(this.pickFar, this.pickNear).normalize();
    return true;
  }

  dispose(): void {
    this.clear();
    this.root.destroy();
  }

  private clear(): void {
    this.worldContentVisible = false;
    for (const state of this.panelStates) {
      state.videoFrameCancel?.();
      state.entity.destroy();
      state.material?.destroy();
      state.videoTexture?.destroy();
      if (state.video) {
        state.video.pause();
        state.video.src = '';
      }
    }
    this.panelStates.length = 0;
    this.panelRegistry.clear();
    this.textureScrolls.length = 0;

    for (const item of this.interactables) {
      item.entity.destroy();
      item.normalMaterial.destroy();
      item.hoverMaterial.destroy();
    }
    this.interactables.length = 0;
    this.interactableById.clear();
    this.rayOccluders.length = 0;

    for (const label of this.labels) {
      label.entity.destroy();
      label.material.destroy();
    }
    this.labels.length = 0;
    this.labelById.clear();

    for (const asset of this.assets) {
      this.app.assets.remove(asset);
      asset.unload();
    }
    this.assets.length = 0;

    for (const audio of this.audioPool) {
      audio.pause();
      audio.src = '';
    }
    this.audioPool.length = 0;

    this.zones = [];
    this.zoneActive.clear();
    this.hoveredId = null;
    this.tapTargetActive = false;
  }
}
